import { EnvironmentalEffects } from "@/components/environment/environmental-effects";
import { NightOverlay } from "@/components/environment/lighting-effects";
import { PokemonPortrait } from "@/components/pokemon/pokemon-portrait";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { AnimatedVFX } from "@/components/vfx/animated-vfx";
import { typewriterEffect } from "@/lib/cutscene/CutsceneEngine";
import { useEffect, useState, useCallback, useRef } from "react";
import { Direction } from "types/battle-engine/battle";

interface Scene {
  id: string;
  background: string;
  text: string;
  speaker: string;
  speakerPortrait: string;
  weather?: "clouds" | "rain" | "snow" | "none";
  pokemons?: Array<{
    name: string;
    position: { x: number; y: number };
    direction: Direction;
    scale: number;
    animation?: string;
  }>;
  vfx?: Array<{
    id: string;
    path: string;
    position: { x: number; y: number };
    scale: number;
    speed?: number;
    loops?: number;
  }>;
  montage?: string[];
}

const INTRO_SCENES: Scene[] = [
  {
    id: "welcome",
    background: "/assets/onboarding/intro-bg1.jpg",
    speaker: "Celebi",
    speakerPortrait: "celebi",
    text: "Hello adventurer! Glad to have you here. All help is welcome.",
    weather: "clouds",
    pokemons: [
      {
        name: "celebi",
        position: { x: 50, y: 50 },
        direction: Direction.S,
        scale: 3,
      },
    ],
  },
  {
    id: "history",
    background: "/assets/onboarding/intro-bg2.jpg",
    speaker: "Celebi",
    speakerPortrait: "celebi",
    text: "This world was never an oasis of peace...",
    weather: "clouds",
    pokemons: [
      {
        name: "parasect",
        position: { x: 30, y: 55 },
        direction: Direction.NE,
        scale: 2,
        animation: "Attack",
      },
      {
        name: "armaldo",
        position: { x: 45, y: 60 },
        direction: Direction.NE,
        scale: 2,
        animation: "Idle",
      },
      {
        name: "sableye",
        position: { x: 55, y: 50 },
        direction: Direction.SW,
        scale: 2,
        animation: "Attack",
      },
      {
        name: "absol",
        position: { x: 70, y: 55 },
        direction: Direction.SW,
        scale: 2,
        animation: "Idle",
      },
    ],
  },
  {
    id: "threat",
    background: "/assets/onboarding/intro-bg2.jpg",
    speaker: "Celebi",
    speakerPortrait: "celebi",
    text: "But this is different. The world faces a new threat.",
    weather: "clouds",
    pokemons: [
      {
        name: "darkrai",
        position: { x: 50, y: 60 },
        direction: Direction.S,
        scale: 3.5,
        animation: "Charge",
      },
    ],
    vfx: [
      {
        id: "fire-1",
        path: "/assets/spells/tile-fx/fire-1.png",
        position: { x: 20, y: 50 },
        scale: 2,
        loops: 100,
        speed: 0.4,
      },
      {
        id: "fire-2",
        path: "/assets/spells/tile-fx/fire-1.png",
        position: { x: 100, y: 50 },
        scale: 2,
        loops: 100,
        speed: 0.4,
      },
      {
        id: "dark-2",
        path: "/assets/spells/impact/fantasy-black-1.png",
        position: { x: 25, y: 70 },
        scale: 4,
        loops: 100,
        speed: 0.4,
      },
      {
        id: "ghost-1",
        path: "/assets/spells/vfx/scary_face_000__18.png",
        position: { x: 52, y: 35 },
        scale: 2,
        loops: 100,
        speed: 0.3,
      },
    ],
  },
  {
    id: "disappeared",
    background: "bg-pk-night-2",
    speaker: "Celebi",
    speakerPortrait: "celebi",
    text: "Our strongest allies, all disappeared...",
    montage: ["rayquaza", "mew", "cresselia", "lugia", "xerneas"],
  },
];

export const IntroView = ({ nextStep }: { nextStep: () => void }) => {
  const [sceneIndex, setSceneIndex] = useState(0);
  const [montageIndex, setMontageIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [battleTick, setBattleTick] = useState(false);
  const [initOverlayOpacity, setInitOverlayOpacity] = useState(1);
  const abortControllerRef = useRef<AbortController | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const currentScene = INTRO_SCENES[sceneIndex];

  if (!currentScene) return null;

  const startTyping = useCallback(async (text: string) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsTyping(true);
    setDisplayedText("");

    try {
      await typewriterEffect(
        text,
        30,
        (updatedText) => {
          setDisplayedText(updatedText);
        },
        abortControllerRef.current.signal,
      );
    } catch (e) {
      // Ignore abort errors
    } finally {
      setIsTyping(false);
    }
  }, []);

  useEffect(() => {
    // Initial fade in
    const interval = setInterval(() => {
      setInitOverlayOpacity((prev) => {
        if (prev <= 0) {
          clearInterval(interval);
          return 0;
        }
        return prev - 0.05;
      });
    }, 50);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setBattleTick((prev) => !prev);
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (currentScene.montage) {
      const interval = setInterval(() => {
        setMontageIndex((prev) => (prev + 1) % currentScene.montage!.length);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [sceneIndex, currentScene.montage]);

  useEffect(() => {
    startTyping(currentScene.text);
    setMontageIndex(0);
  }, [sceneIndex, startTyping, currentScene.text]);

  const handleAdvance = () => {
    if (isTyping) {
      // Skip typing
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setDisplayedText(currentScene.text);
      setIsTyping(false);
      return;
    }

    if (sceneIndex < INTRO_SCENES.length - 1) {
      setSceneIndex((prev) => prev + 1);
    } else {
      nextStep();
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full overflow-hidden cursor-pointer ${
        currentScene.background.startsWith("bg-") ? currentScene.background : ""
      }`}
      onClick={handleAdvance}
      style={{
        backgroundImage: currentScene.background.startsWith("/")
          ? `url("${currentScene.background}")`
          : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        transition: "background-image 1s ease-in-out",
      }}
    >
      {/* Cinematic Bars */}
      <div className="absolute w-full h-14 top-0 left-0 bg-black z-[9999]"></div>
      <div className="absolute w-full h-14 bottom-0 left-0 bg-black z-[9999]"></div>

      {/* Initial Fade Overlay */}
      <div
        className="absolute inset-0 bg-black z-[10001] pointer-events-none"
        style={{ opacity: initOverlayOpacity }}
      ></div>

      {/* Weather Effects */}
      {currentScene.weather && currentScene.weather !== "none" && (
        <EnvironmentalEffects weather={currentScene.weather} />
      )}

      {/* Night Overlay for Darkrai scene */}
      <NightOverlay opacity={currentScene.id === "threat" ? 0.8 : 0} />

      {/* Scene Pokemons */}
      {currentScene.pokemons?.map((p, i) => (
        <div
          key={`${sceneIndex}-${p.name}-${i}`}
          className="absolute transition-opacity duration-1000"
          style={{
            left: `${p.position.x}%`,
            top: `${p.position.y}%`,
            transform: "translate(-50%, -50%)",
          }}
        >
          <PokemonSprite
            pokemon={{
              base_pokemon_name: p.name,
            }}
            direction={p.direction}
            animation={
              currentScene.id === "history"
                ? p.animation === "Attack"
                  ? battleTick
                    ? "Charge"
                    : "Idle"
                  : battleTick
                  ? "Idle"
                  : "Charge"
                : p.animation || "Idle"
            }
            style={{
              scale: p.scale,
            }}
            showShadow
          />
        </div>
      ))}

      {/* Montage Legendaries */}
      {currentScene.montage?.map((name, i) => (
        <div
          key={`montage-${name}-${i}`}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-1000"
          style={{
            opacity: montageIndex === i ? 0.4 : 0,
            filter: "grayscale(100%)",
            pointerEvents: "none",
          }}
        >
          <PokemonSprite
            pokemon={{ base_pokemon_name: name }}
            direction={Direction.SE}
            animation="Idle"
            style={{ scale: 3.5 }}
            showShadow
          />
        </div>
      ))}

      {/* Scene VFX */}
      {currentScene.vfx?.map((v) => {
        const rect = containerRef.current?.getBoundingClientRect();
        const width = rect?.width || window.innerWidth;
        const height = rect?.height || window.innerHeight;

        return (
          <AnimatedVFX
            key={v.id}
            id={v.id}
            spriteUrl={v.path}
            position={{
              x: (v.position.x / 100) * width,
              y: (v.position.y / 100) * height,
            }}
            spriteScale={v.scale}
            speed={v.speed || 1}
            loops={v.loops || 1}
          />
        );
      })}

      {/* RPG Chat Box */}
      <div className="absolute w-[90%] max-w-[500px] bottom-[8%] left-1/2 -translate-x-1/2 z-[10000]">
        <div
          className="relative border-4 border-pk-beige-4/50 rounded-lg bg-pk-purple-1 p-1
          before:content-[''] before:absolute before:inset-[-10px] before:border-2 before:border-pk-beige-4/30 before:rounded-xl before:pointer-events-none
        "
        >
          <div className="bg-pk-purple-2 border-2 border-white/20 rounded-md p-4 flex flex-col gap-2">
            {/* Speaker Header */}
            <div className="flex items-center gap-3 -mt-10 mb-2">
              <div className="bg-pk-purple-1 p-1 border-2 border-white rounded-xl shadow-lg">
                <PokemonPortrait
                  pokemon={{
                    base_pokemon_name: currentScene.speakerPortrait,
                    is_shiny: false,
                  }}
                  emotion="Normal"
                  className="w-16 h-16"
                />
              </div>
              <div className="bg-pk-purple-1 px-4 py-1 border-2 border-white rounded-full shadow-md">
                <StrokedText
                  fontSize={20}
                  strokeWidth={6}
                  className="text-pk-yellow-1"
                >
                  {currentScene.speaker}
                </StrokedText>
              </div>
            </div>

            {/* Dialog Text */}
            <div className="min-h-[80px]">
              <p className="text-white text-lg font-medium leading-relaxed">
                {displayedText}
                {isTyping && (
                  <span className="inline-block w-2 h-5 bg-white ml-1 animate-pulse" />
                )}
              </p>
            </div>

            {/* Advance Indicator */}
            {!isTyping && (
              <div className="flex justify-end mt-1">
                <div className="animate-bounce text-pk-yellow-1 text-xl">▼</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
